import React from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import styled, { css } from "styled-components";
import GoogleLogo from "./Icons/GoogleLogo";
import RightArrow from "./Icons/RightArrow";
import { rem } from "polished";

/* 
Examples:
  <Button kind="primary">Redeem</Button>
  <Button kind="primary" disabled>Redeem Disabled</Button>
  <Button kind="secondary">Cancel</Button>
  <Button kind="secondary" disabled>Cancel Disabled</Button>
  <Button kind="primary" inverted>Primary Special</Button>
  <Button kind="secondary" inverted>Secondary Special</Button>

  // Optional Width - pixels will be converted to rems, default is 100% of container
  <Button kind="primary" width="200px">Redeem</Button>
*/

type ButtonProps = {
  // Specify the content of your Button
  children: string;
  // Specify the kind of Button you want to create
  kind:
    | "primary"
    | "secondary"
    | "success"
    | "bei2"
    | "rewards_overview"
    | "error";
  // Specify whether the Button should be disabled, or not
  disabled?: boolean;
  // Specify whether the Button colors should be inverted, or not
  inverted?: boolean;
  // Specify the width of the button - default is 100% of the container
  width?: string;
  // Optionally specify an href for your Button to become an <a> element
  href?: string;
  // Optionally specify if targetBlank is true to open new tab
  targetBlank?: boolean;
  // Optionally specify an type for your Button to become an <input> element
  type?: string;
  // Optionally specify an onClick for your Button
  onClick?: () => void;
  // Optionally specify whether button is inline
  inline?: boolean;
  // Optionally specify whether button has loading animation
  loading?: boolean;
  // Optionally specify whether button has class prop
  className?: string;
  // Optionally specify color for button
  color?: string;
  // Optionally whether button is small version
  small?: boolean;
  // Optionally wheter button is google button
  isGoogle?: boolean;
  // Optionally wheter arrow should be inside the button
  hasArrow?: boolean;
  // Optionally wheter arrow should be on the left or right
  arrowPosition?: "left" | "right";
};

type StyledButtonProps = {
  kind: string;
  width?: string;
  inverted?: boolean;
  disabled?: boolean;
  inline?: boolean;
  small?: boolean;
  isGoogle?: boolean;
};

const StyledButton = styled.button<StyledButtonProps>`
  && {
    // Base Button
    cursor: pointer;
    min-width: ${({ small }) => (small ? "unset" : "150px")};
    padding: ${({ small, isGoogle }) =>
      isGoogle ? "0px" : small ? "8px 12px" : "14px"} !important;
    font-family: ${({ isGoogle, theme }) =>
      isGoogle ? "Roboto" : theme.secondaryFont || "inherit"};
    font-size: 16px;
    line-height: 16px;
    font-weight: 500;
    width: ${({ width, small }) => (small ? "fit-content" : width || "100%")};
    height: ${({ small }) => (small ? "35px" : "auto")};
    transition: all 0.4s ease-in-out;
    border: none;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:focus {
      outline: none;
    }

    // Google
    ${({ isGoogle }) =>
      isGoogle &&
      css`
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 228px;
      `}

    // Primary
    ${({ kind, inverted, theme, color, isGoogle }) =>
      kind === "primary" &&
      css`
        background-color: ${isGoogle
          ? "#4285f4"
          : inverted
          ? "#fff"
          : color
          ? `#${color}`
          : theme.btnColor};
        color: ${inverted ? (color ? `#${color}` : theme.btnColor) : "#fff"};
        transition: all 0.2s ease-in-out;
      `}

    // Primary & Disabled
    ${({ kind, disabled }) =>
      kind === "primary" &&
      disabled &&
      css`
        background-color: #efefef;
        color: #999999;
        pointer-events: none;
      `}

    // BEI 2.0 & Rewards Overview
    ${({ kind, inverted, theme, color, isGoogle }) =>
    (kind === "bei2" || kind === "rewards_overview") &&
    css`
      background-color: ${isGoogle
        ? "#4285f4"
        : inverted
        ? "#fff"
        : color
        ? `#${color}`
        : theme.primaryColor};
      color: ${inverted ? (color ? `#${color}` : theme.primaryColor) : "#fff"};
      transition: all 0.2s ease-in-out;
    `}

    //  Secondary
    ${({ kind, inverted, theme, color }) =>
      kind === "secondary" &&
      css`
        background-color: ${color ? `#${color}` : "transparent"};
        color: ${inverted ? "#fff" : theme.btnPressedColor};
        box-shadow: 2px 2px 12px rgba(0, 0, 0, 0.12);
        transition: all 0.2s ease-in-out;
        &:hover {
          color: ${theme.btnHoverColor};
        }
      `}

    // Secondary & Disabled
    ${({ kind, disabled }) =>
      kind === "secondary" &&
      disabled &&
      css`
        color: #cbcbcb;
        pointer-events: none;
      `}

    // Success & Disabled
    ${({ kind, disabled }) =>
      kind === "success" &&
      disabled &&
      css`
        background-color: ${({ theme }) => theme.successNotificationColor};
        color: #fff;
        pointer-events: none;
      `}

    // Error & Disabled
    ${({ kind, disabled }) =>
      kind === "error" &&
      disabled &&
      css`
        background-color: ${({ theme }) => theme.notificationsColor};
        color: #fff;
        pointer-events: none;
      `}

    // Inline button
    ${({ inline }) =>
      inline === true &&
      css`
        min-width: unset;
        line-height: ${rem("0px")};
      `}

    // Inline button
    ${({ inline }) =>
      inline === false &&
      css`
        min-width: ${rem("241px")};

        ${({ theme }) => theme.tablet`
          max-width: ${rem("513px")};
          padding: ${rem("14px")};
          width: 100%;
        `}
      `}
  }
`;

const StyledSpinner = styled.span`
  position: relative;
  height: 22px !important;
  width: 22px !important;

  .MuiCircularProgress-colorPrimary {
    height: 22px !important;
    width: 22px !important;
    color: ${({ theme }) => theme.primaryColor};
  }
`;

const StyledChildren = styled.div<{ arrowPosition?: "left" | "right" }>`
  margin-left: ${({ arrowPosition }) =>
    arrowPosition === "left" ? "8px" : "4px"};
  margin-right: ${({ arrowPosition }) =>
    arrowPosition === "right" ? "8px" : "4px"};
  display: flex;
  align-items: center;
`;

const Button = ({
  kind,
  disabled,
  inverted,
  inline,
  width,
  href,
  targetBlank,
  onClick,
  type,
  loading,
  children,
  className,
  color = "",
  small,
  isGoogle,
  hasArrow,
  arrowPosition,
}: ButtonProps) => {
  if (loading) {
    return (
      <StyledSpinner>
        <CircularProgress />
      </StyledSpinner>
    );
  } else if (href && !disabled) {
    if (targetBlank) {
      return (
        <a className={className} href={href} target="_blank" rel="noreferrer">
          <StyledButton
            kind={kind}
            disabled={disabled}
            width={width}
            inverted={inverted}
            small={small}
            inline={inline}
            color={color}
          >
            {children}
          </StyledButton>
        </a>
      );
    } else {
      return (
        <a href={href}>
          <StyledButton
            kind={kind}
            disabled={disabled}
            width={width}
            inverted={inverted}
            small={small}
            inline={inline}
            color={color}
          >
            {children}
          </StyledButton>
        </a>
      );
    }
  } else if (isGoogle) {
    return (
      <StyledButton
        className={className}
        onClick={onClick}
        kind={kind}
        disabled={disabled}
        width={width}
        inverted={inverted}
        small={small}
        inline={inline}
        isGoogle={isGoogle}
      >
        <GoogleLogo />
        <StyledChildren>{children}</StyledChildren>
      </StyledButton>
    );
  } else if (hasArrow) {
    return (
      <StyledButton
        className={className}
        onClick={onClick}
        kind={kind}
        disabled={disabled}
        width={width}
        inverted={inverted}
        small={small}
        inline={inline}
        color={color}
      >
        {arrowPosition === "left" && <RightArrow disabled={disabled} />}
        <StyledChildren>{children}</StyledChildren>
        {arrowPosition === "right" && <RightArrow disabled={disabled} />}
      </StyledButton>
    );
  } else if (type && onClick && !disabled) {
    return (
      <StyledButton
        className={className}
        as="input"
        value={children}
        type={type}
        onClick={onClick}
        kind={kind}
        disabled={disabled}
        width={width}
        inverted={inverted}
        small={small}
        inline={inline}
        color={color}
      />
    );
  } else if (type && !disabled) {
    return (
      <StyledButton
        className={className}
        as="input"
        value={children}
        type={type}
        onClick={onClick}
        kind={kind}
        disabled={disabled}
        width={width}
        inverted={inverted}
        small={small}
        inline={inline}
        color={color}
      />
    );
  } else {
    return (
      <StyledButton
        className={className}
        kind={kind}
        onClick={onClick}
        disabled={disabled}
        width={width}
        inverted={inverted}
        small={small}
        inline={inline}
        color={color}
      >
        {children}
      </StyledButton>
    );
  }
};

export default Button;
